/** Apple Services — Monthly Reports tab (immutable snapshots, PDF/XLSX, schedule). */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { FileDown, Send } from "lucide-react";

export function AppleMonthlyReports() {
  const [items, setItems] = useState([]);
  const [sched, setSched] = useState({ enabled: false, day_of_month: 1, recipients: [] });
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [recipients, setRecipients] = useState("");

  const load = () => {
    api.get("/apple-services/reports/monthly").then(({ data }) => setItems(data.items || []));
    api.get("/apple-services/reports/monthly-schedule").then(({ data }) => {
      setSched(data.schedule || {});
      setRecipients((data.schedule?.recipients || []).join(", "));
    });
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    try {
      const { data } = await api.post("/apple-services/reports/monthly/generate", { month, force: false });
      toast.success(data.already_existed
        ? `Report for ${month} already exists (immutable)`
        : `Generated ${month}: ${data.report.total_dep} DEP · ${data.report.total_applecare} AppleCare+`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Generate failed"); }
  };
  const saveSchedule = async () => {
    try {
      await api.put("/apple-services/reports/monthly-schedule", {
        enabled: sched.enabled, day_of_month: Number(sched.day_of_month) || 1,
        recipients: recipients.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Schedule saved"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const dl = async (r, fmt) => {
    try {
      const res = await api.get(`/apple-services/reports/monthly/${r.id}/${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `apple-monthly-${r.month}.${fmt}`; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };
  const send = async (r) => {
    try {
      const { data } = await api.post(`/apple-services/reports/monthly/${r.id}/send`);
      toast.success(`Sent to ${data.sent} recipient(s)`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
  };

  return (
    <div className="space-y-4" data-testid="apple-monthly-reports">
      <div className="flex items-end gap-2 flex-wrap">
        <label className="text-[10px] text-slate-500">Month
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
              data-testid="amr-month"
              className="mt-1 block text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
        </label>
        <button onClick={generate} data-testid="amr-generate"
            className="text-[11px] px-2.5 py-1.5 rounded-md bg-slate-900 text-white">
          Generate immutable snapshot</button>
      </div>

      <div className="border border-slate-200 rounded-lg bg-white p-3 space-y-2" data-testid="amr-schedule">
        <div className="text-[11px] font-semibold text-slate-700">Scheduled delivery</div>
        <div className="flex items-end gap-3 flex-wrap">
          <label className="text-[10px] text-slate-600 flex items-center gap-1.5">
            <input type="checkbox" checked={!!sched.enabled}
                onChange={(e) => setSched({ ...sched, enabled: e.target.checked })}
                data-testid="amr-sched-enabled" /> Enabled
          </label>
          <label className="text-[10px] text-slate-500">Day of month
            <input type="number" min={1} max={28} value={sched.day_of_month || 1}
                onChange={(e) => setSched({ ...sched, day_of_month: e.target.value })}
                className="mt-1 block w-20 text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
          </label>
          <label className="text-[10px] text-slate-500 flex-1 min-w-[280px]">Recipients (comma separated)
            <input value={recipients} onChange={(e) => setRecipients(e.target.value)}
                data-testid="amr-recipients"
                className="mt-1 block w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
          </label>
          <button onClick={saveSchedule} data-testid="amr-sched-save"
              className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-300">Save schedule</button>
        </div>
      </div>

      <div className="border border-slate-200 rounded-lg bg-white">
        <table className="w-full text-[11.5px]">
          <thead className="bg-slate-50 text-slate-500"><tr>
            <th className="text-left px-3 py-2">Month</th>
            <th className="text-right px-3 py-2">DEP</th>
            <th className="text-right px-3 py-2">AppleCare+</th>
            <th className="text-right px-3 py-2">Customers</th>
            <th className="text-left px-3 py-2">Generated</th>
            <th className="text-right px-3 py-2 w-52"></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 px-3 py-6">
              No monthly reports yet — pick a month and generate the first snapshot.</td></tr>}
            {items.map((r) => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`amr-row-${r.month}`}>
                <td className="px-3 py-2 font-mono font-semibold">{r.month}</td>
                <td className="px-3 py-2 text-right font-mono">{r.total_dep}</td>
                <td className="px-3 py-2 text-right font-mono">{r.total_applecare}</td>
                <td className="px-3 py-2 text-right font-mono">{r.customer_count}</td>
                <td className="px-3 py-2 text-[10px] text-slate-500">{r.generated_by}
                  {r.last_sent_at && <div className="text-emerald-700">sent {r.last_sent_at.slice(0, 10)}</div>}</td>
                <td className="px-3 py-2 text-right space-x-1">
                  <button onClick={() => dl(r, "pdf")} data-testid={`amr-pdf-${r.month}`}
                      className="text-[10px] px-2 py-1 rounded border border-slate-300 inline-flex items-center gap-1">
                    <FileDown className="h-3 w-3" />PDF</button>
                  <button onClick={() => dl(r, "xlsx")} data-testid={`amr-xlsx-${r.month}`}
                      className="text-[10px] px-2 py-1 rounded border border-slate-300 inline-flex items-center gap-1">
                    <FileDown className="h-3 w-3" />XLSX</button>
                  <button onClick={() => send(r)} data-testid={`amr-send-${r.month}`}
                      className="text-[10px] px-2 py-1 rounded bg-emerald-800 text-white inline-flex items-center gap-1">
                    <Send className="h-3 w-3" />Send</button>
                </td>
              </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AppleMonthlyReports;
